import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Paper,
  CardContent,
  Snackbar,
  Alert,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import type { AlertColor } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import LevelIcon from '@mui/icons-material/SignalCellularAlt';
import LockIcon from '@mui/icons-material/Lock';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import WebinarIcon from '@mui/icons-material/Event';
import JoinIcon from '@mui/icons-material/PlayCircle';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import RecommendedIcon from '@mui/icons-material/AutoStories';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import CertificateIcon from '@mui/icons-material/EmojiEvents';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Link } from 'react-router-dom';
import { Card } from '@mui/material';
import DOMPurify from 'dompurify';
import BookmarkButton from '../components/BookmarkButton';
import CourseCreditLimitHint from '../components/CourseCreditLimitHint';
import AIAssistant from '../components/AIAssistant';
import NasbaDisclosures from '../components/NasbaDisclosures';
import { getEligibleWebinarInstances, type Course } from '../services/courseService';
import { getFieldsOfStudy } from '../utils/fieldOfStudy';
import type { Registration } from '../services/registrationService';
import registrationService, { isRegistrationActive } from '../services/registrationService';
import webinarRegistrationService from '../services/webinarRegistrationService';
import type { AddonAccessCheck } from '../services/billingService';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { useUserContext } from '../hooks/useUserContext';
import { getApiErrorInfo } from '../services/api';
import { parseApiDateAsUtc } from '../services/webinarTiming';
import TenantUpgradeCtaComponent from '../components/TenantUpgradeCta';
import type { AdminSelfAssignResult } from '../hooks/useAdminSelfAssign';

interface UiNotificationState {
  open: boolean;
  severity: AlertColor;
  message: string;
}

function formatDateTime(dateStr?: string) {
  if (!dateStr) return null;
  return parseApiDateAsUtc(dateStr).toLocaleString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '');
}

function formatCreditLabel(course: Course): string {
  const hours = course.creditHours;
  const categories = [...new Set((course.courseCredits ?? []).map(c => c.educationCategoryName).filter(Boolean))];
  const label = `${hours} CPE Credit${hours !== 1 ? 's' : ''}`;
  return categories.length > 0 ? `${label} · ${categories.join(', ')}` : label;
}

interface WebinarCourseDetailProps {
  course: Course;
  courseRegistrations: Registration[];
  regLoading: boolean;
  addonCheck: AddonAccessCheck | null;
  addonCheckLoading: boolean;
  recommendedCourses: Course[];
  requestUpgradeLoading: boolean;
  requestUpgradeSuccess: boolean;
  selectedInstanceId: number | null;
  onInstanceSelect: (instanceId: number | null) => void;
  onClearSelection: () => void;
  onRequestUpgrade: () => void;
  onRegistrationUpdate: (registration: Registration) => void;
  onReconcileRegistrations?: () => Promise<void>;
  isTenantAdminProp?: boolean;
  adminSelfAssign?: AdminSelfAssignResult;
  onAdminActivate?: () => void;
}

export default function WebinarCourseDetail({
  course,
  courseRegistrations,
  regLoading,
  addonCheck,
  addonCheckLoading,
  recommendedCourses,
  requestUpgradeLoading,
  requestUpgradeSuccess,
  selectedInstanceId,
  onInstanceSelect,
  onClearSelection,
  onRequestUpgrade,
  onRegistrationUpdate,
  onReconcileRegistrations,
  isTenantAdminProp,
  adminSelfAssign,
  onAdminActivate,
}: WebinarCourseDetailProps) {
  const navigate = useNavigate();
  const { loading: accessLoading } = useSubscriptionAccess();
  const { clerkUser, isTenantAdmin } = useUserContext();

  const [isRegistering, setIsRegistering] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isDownloadingCertificate, setIsDownloadingCertificate] = useState(false);
  const [pendingAction, setPendingAction] = useState<'cancel' | 'transfer' | null>(null);
  const [notification, setNotification] = useState<UiNotificationState>({
    open: false,
    severity: 'info',
    message: '',
  });

  const courseId = course.id;
  const isTenantManaged = addonCheck?.isTenantGated ?? false;
  const hasAccess = addonCheck?.hasAccess ?? false;

  const handleRegister = async () => {
    setIsRegistering(true);
    try {
      if (!effectiveSelectedInstanceId) {
        setNotification({ open: true, severity: 'error', message: 'Please select a date to register.' });
        setIsRegistering(false);
        return;
      }
      const name = [clerkUser?.firstName, clerkUser?.lastName].filter(Boolean).join(' ');
      const email = clerkUser?.emailAddresses?.[0]?.emailAddress ?? '';
      const phone = clerkUser?.phoneNumbers?.[0]?.phoneNumber ?? '999-999-9999';

      const registrationResult = await webinarRegistrationService.registerForWebinar(courseId, {
        name,
        email,
        phone,
        courseInstanceId: effectiveSelectedInstanceId,
      });

      if (!registrationResult.success) {
        setNotification({
          open: true,
          severity: 'error',
          message: registrationResult.message,
        });
        return;
      }

      if (registrationResult.registration) {
        onRegistrationUpdate(registrationResult.registration);
      }
      onClearSelection();

      setNotification({
        open: true,
        severity: 'success',
        message: registrationResult.message,
      });
    } catch (err: unknown) {
      const apiError = getApiErrorInfo(err);
      console.error('Registration failed:', err);
      setNotification({
        open: true,
        severity: 'error',
        message: apiError.message,
      });
    } finally {
      setIsRegistering(false);
    }
  };

  const handleTransfer = async () => {
    if (!effectiveSelectedInstanceId) {
      setNotification({ open: true, severity: 'error', message: 'Please select a new date to transfer to.' });
      return;
    }
    setPendingAction('transfer');
  };

  const confirmTransfer = async () => {
    setPendingAction(null);
    setIsTransferring(true);
    try {
      const name = [clerkUser?.firstName, clerkUser?.lastName].filter(Boolean).join(' ');
      const email = clerkUser?.emailAddresses?.[0]?.emailAddress ?? '';
      const phone = clerkUser?.phoneNumbers?.[0]?.phoneNumber ?? '999-999-9999';

      const result = await webinarRegistrationService.transferWebinarRegistration(courseId, {
        name,
        email,
        phone,
        courseInstanceId: effectiveSelectedInstanceId ?? undefined,
      });

      if (!result.success) {
        await onReconcileRegistrations?.();
        setNotification({ open: true, severity: 'error', message: result.message });
        return;
      }

      if (activeRegistration) {
        onRegistrationUpdate({
          ...activeRegistration,
          status: 'transferred',
        });
      }

      if (result.registration) {
        onRegistrationUpdate(result.registration);
      }
      onClearSelection();
      setNotification({ open: true, severity: 'success', message: result.message });
    } catch (err: unknown) {
      await onReconcileRegistrations?.();
      const apiError = getApiErrorInfo(err);
      console.error('Transfer failed:', err);
      setNotification({ open: true, severity: 'error', message: apiError.message });
    } finally {
      setIsTransferring(false);
    }
  };

  const handleJoinWebinar = () => {
    if (currentRegistration?.joinUrl) {
      window.open(currentRegistration.joinUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDownloadCertificate = async () => {
    if (!currentRegistration?.id) {
      return;
    }

    setIsDownloadingCertificate(true);
    try {
      await registrationService.downloadWebinarCertificate(currentRegistration.id, course.title);
    } catch (err: unknown) {
      const apiError = getApiErrorInfo(err);
      setNotification({
        open: true,
        severity: 'error',
        message: apiError.message || 'Unable to download certificate right now.',
      });
    } finally {
      setIsDownloadingCertificate(false);
    }
  };

  const handleCancelRegistration = async () => {
    if (!activeRegistration) {
      setNotification({
        open: true,
        severity: 'error',
        message: 'No active registration was found to cancel.',
      });
      return;
    }
    setPendingAction('cancel');
  };

  const confirmCancelRegistration = async () => {
    setPendingAction(null);
    setIsCancelling(true);
    try {
      const result = await webinarRegistrationService.cancelWebinarRegistration(courseId);

      if (!result.success) {
        await onReconcileRegistrations?.();
        setNotification({ open: true, severity: 'error', message: result.message });
        return;
      }

      if (result.registration) {
        onRegistrationUpdate(result.registration);
      } else {
        onRegistrationUpdate({
          ...currentRegistration!,
          status: 'cancelled',
        });
      }
      onClearSelection();
      setNotification({ open: true, severity: 'success', message: result.message });
    } catch (err: unknown) {
      await onReconcileRegistrations?.();
      const apiError = getApiErrorInfo(err);
      console.error('Cancel failed:', err);
      setNotification({ open: true, severity: 'error', message: apiError.message });
    } finally {
      setIsCancelling(false);
    }
  };

  const availableInstances = getEligibleWebinarInstances(course.upcomingInstances);
  const activeRegistration = courseRegistrations.find(isRegistrationActive) ?? null;
  const completedRegistration = courseRegistrations.find((item) => item.status === 'completed') ?? null;
  const cancelledOrTransferredInstanceIds = new Set(
    courseRegistrations
      .filter((item) => item.status === 'cancelled' || item.status === 'transferred')
      .map((item) => item.courseInstanceId)
      .filter((id): id is number => typeof id === 'number')
  );
  const effectiveSelectedInstanceId =
    selectedInstanceId !== null && cancelledOrTransferredInstanceIds.has(selectedInstanceId)
      ? null
      : selectedInstanceId;
  const selectedInstance =
    effectiveSelectedInstanceId !== null
      ? availableInstances.find((instance) => instance.id === effectiveSelectedInstanceId) ?? null
      : null;
  const currentRegistration = activeRegistration ?? completedRegistration;
  const isRegistered = !!currentRegistration;
  const registeredInstanceId = currentRegistration?.courseInstanceId ?? null;
  const isCompleted = currentRegistration?.status === 'completed';
  const activeInstance =
    registeredInstanceId !== null
      ? availableInstances.find((instance) => instance.id === registeredInstanceId) ?? null
      : null;
  const activeStartDate = currentRegistration?.startDate ?? activeInstance?.startDate;
  const primaryDescription =
    course.description ||
    course.descriptions?.find((section) => !!section.description)?.description ||
    undefined;
  const courseDescriptionForAi = primaryDescription
    ? stripHtml(primaryDescription).replace(/\s+/g, ' ').trim().slice(0, 700)
    : undefined;
  const fieldsOfStudy = getFieldsOfStudy(course);
  const aiCourseContext = {
    courseId: course.id,
    title: course.title,
    category: fieldsOfStudy.join(', '),
    courseType: 'webinar',
    creditHours: course.creditHours,
    level: course.level,
    description: courseDescriptionForAi,
    startDate: course.startDate,
    endDate: course.endDate,
    selectedInstanceId: selectedInstance?.id ?? null,
    selectedInstanceStartDate: selectedInstance?.startDate,
    selectedInstanceEndDate: selectedInstance?.endDate,
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/app/courses')}
          sx={{
            textTransform: 'uppercase',
            color: 'primary.main',
            width: 'fit-content',
            fontSize: '14px',
            fontWeight: 500,
            letterSpacing: '0.4px',
            px: 1,
            py: 0.75,
          }}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BookmarkButton courseId={courseId} size="medium" />
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 400, fontSize: { xs: '22px', sm: '26px', md: '32px' }, lineHeight: 1.235, letterSpacing: '0.25px', flex: 1 }}
          >
            {course.title}
          </Typography>
        </Box>
      </Box>

      {/* Meta row */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box
          component="span"
          sx={(theme) => ({
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            px: 0.875,
            py: '3px',
            borderRadius: '4px',
            bgcolor: theme.palette.status.webinar.bg,
            color: theme.palette.status.webinar.fg,
          })}
        >
          Webinar
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AccessTimeIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary' }}>
            {course.creditHours} CPE Hour{course.creditHours !== 1 ? 's' : ''}
            {course.courseCredits?.length > 0 && ` · ${[...new Set(course.courseCredits.map(c => c.educationCategoryName).filter(Boolean))].join(', ')}`}
          </Typography>
        </Box>
        {course.level && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <LevelIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary' }}>
              {course.level}
            </Typography>
          </Box>
        )}
        {course.startDate && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CalendarIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary' }}>
              {formatDateTime(course.startDate)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Credit-limit hint: this course's delivery method is at/near a cap on one of the user's licenses */}
      <CourseCreditLimitHint course={course} sx={{ mb: 3 }} />

      {/* Three column layout */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          lg: '1fr 360px',
          xl: '1fr 360px 320px',
        },
        gridTemplateAreas: {
          xs: '"main" "registration" "similar" "ai"',
          lg: '"main sidebar" "similar sidebar"',
          xl: '"main registration ai" "similar registration ai"',
        },
        gridAutoRows: 'auto',
        gap: { xs: 2, lg: 3 },
        alignItems: 'start',
      }}>
        {/* Main Content */}
        <Box sx={{ gridArea: 'main', minWidth: 0 }}>
          {/* Main Details Card */}
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              {/* Instructor */}
              {course.speakers.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar
                    src={course.speakers[0]?.avatarUrl ?? undefined}
                    sx={{
                      width: 84,
                      height: 84,
                      bgcolor: 'rgba(0, 0, 0, 0.07)',
                      borderRadius: '6px',
                      flexShrink: 0,
                      '& img': { objectFit: 'contain' },
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                  </Avatar>
                  <Box>
                    <Typography
                      component="div"
                      sx={{
                        fontSize: '10px',
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        lineHeight: 1,
                        mb: 0.75,
                      }}
                    >
                      Instructor
                    </Typography>
                    <Typography
                      sx={{ fontSize: '16px', fontWeight: 600, color: 'text.primary', lineHeight: 1.4, letterSpacing: '-0.01em' }}
                    >
                      {course.speakers.map((s) => (s.displayName || s.name || '').trim()).filter(Boolean).join(', ')}
                    </Typography>
                    {course.speakers[0]?.title && (
                      <Typography
                        sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.25, lineHeight: 1.4 }}
                      >
                        {course.speakers[0].title}
                      </Typography>
                    )}
                    {course.developer && (
                      <Typography
                        sx={{ fontSize: '11px', color: 'text.disabled', mt: 0.5, lineHeight: 1.4 }}
                      >
                        Developed by {course.developer}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Description */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 1, px: 2 }}
                >
                  Description
                </Typography>
                <Typography
                  sx={{ fontSize: '15px', lineHeight: 1.6, color: 'text.primary', px: 2, maxWidth: '72ch' }}
                >
                  {course.description?.replace(/<[^>]*>/g, '') ?? ''}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Field of Study */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.5, px: 2 }}
                >
                  Field of Study
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '14px', px: 2 }}>
                  {fieldsOfStudy.length > 0 ? fieldsOfStudy.join(', ') : '—'}
                </Typography>
              </Box>

              {/* Webinar-specific sections from synced descriptions */}
              {course.descriptions.find(d => d.descriptionType === 'whoShouldAttend')?.description && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.5, px: 2 }}
                    >
                      Who should attend
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '14px', px: 2 }}>
                      {course.descriptions.find(d => d.descriptionType === 'whoShouldAttend')!.description.replace(/<[^>]*>/g, '')}
                    </Typography>
                  </Box>
                </>
              )}

              {course.level && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.5, px: 2 }}
                    >
                      Program Level
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '14px', px: 2 }}>
                      {course.level}
                    </Typography>
                  </Box>
                </>
              )}

              {course.descriptions.find(d => d.descriptionType === 'programContent')?.description && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography
                      sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.5, px: 2 }}
                    >
                      Program Content
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '14px', px: 2, lineHeight: 1.43 }}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(course.descriptions.find(d => d.descriptionType === 'programContent')!.description),
                      }}
                    />
                  </Box>
                </>
              )}

              <NasbaDisclosures course={course} />
            </CardContent>
          </Paper>
        </Box>

        {/* Similar Courses */}
        <Box sx={{ gridArea: 'similar', minWidth: 0 }}>
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <RecommendedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '18px' }}>
                  Similar Courses
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
                {recommendedCourses.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No recommendations available.</Typography>
                ) : (
                  recommendedCourses.map((rec) => (
                    <Card
                      key={rec.id}
                      elevation={0}
                      sx={{
                        minWidth: 228,
                        maxWidth: 228,
                        flexShrink: 0,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '4px',
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Chip
                          label={formatCreditLabel(rec)}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mb: 1, borderRadius: '100px', height: '24px', fontSize: '12px' }}
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 500, fontSize: '16px', lineHeight: 1.5, letterSpacing: '0.15px', mb: 0.5 }}
                        >
                          {rec.title}
                        </Typography>
                        {rec.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 1.5, lineHeight: 1.66, letterSpacing: '0.4px' }}
                          >
                            {stripHtml(rec.description).slice(0, 90)}
                            {stripHtml(rec.description).length > 90 ? '...' : ''}
                          </Typography>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          component={Link}
                          to={`/app/courses/${rec.id}`}
                          endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                          sx={{
                            textTransform: 'uppercase',
                            fontSize: '14px',
                            letterSpacing: '0.46px',
                            fontWeight: 500,
                          }}
                        >
                          View Course
                        </Button>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Box>
            </CardContent>
          </Paper>
        </Box>

        {/* Sidebar wrapper: single flex column at lg; transparent (display:contents) at xs/xl */}
        <Box sx={{
          gridArea: { lg: 'sidebar' },
          display: { xs: 'contents', lg: 'flex', xl: 'contents' },
          flexDirection: 'column',
          gap: 2,
          minWidth: 0,
        }}>

        {/* Sidebar */}
        <Box sx={{ gridArea: 'registration', minWidth: 0 }}>
          {/* Registration Card */}
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '18px', mb: 2, px: 2 }}>
                Registration
              </Typography>

              {/* Date/Time Selection */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 2 }}>
                Select a date/time:
              </Typography>
              <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {availableInstances.length > 0 ? (
                  availableInstances.map((instance) => {
                    const isRegisteredInstance =
                      isRegistered && registeredInstanceId !== null && instance.id === registeredInstanceId;
                    const isSelected = effectiveSelectedInstanceId === instance.id;
                    const cancelledReg = courseRegistrations.find(
                      (r) => r.courseInstanceId === instance.id && (r.status === 'cancelled' || r.status === 'transferred')
                    );
                    const isCancelledOrTransferred = !isRegisteredInstance && !!cancelledReg;
                    const chipLabel = isCancelledOrTransferred
                      ? `${formatDateTime(instance.startDate) || 'No date available'} · ${cancelledReg!.status === 'transferred' ? 'Transferred' : 'Cancelled'}`
                      : (formatDateTime(instance.startDate) || 'No date available');

                    return (
                      <Chip
                        key={instance.id}
                        label={chipLabel}
                        onClick={isRegisteredInstance || isCancelledOrTransferred ? undefined : () => isSelected ? onClearSelection() : onInstanceSelect(instance.id)}
                        disabled={isRegisteredInstance || isCancelledOrTransferred}
                        icon={isRegisteredInstance ? <CheckCircleIcon /> : undefined}
                        color={isRegisteredInstance ? 'success' : (isSelected ? 'primary' : 'default')}
                        variant={isRegisteredInstance || isSelected ? 'filled' : 'outlined'}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          minHeight: { xs: 44, sm: 36 },
                          py: { xs: 1, sm: 0.5 },
                          justifyContent: 'flex-start',
                          fontSize: '14px',
                          borderRadius: '100px',
                          cursor: isRegisteredInstance || isCancelledOrTransferred ? 'default' : 'pointer',
                          '& .MuiChip-label': { whiteSpace: 'normal', display: 'block' },
                          '&.Mui-disabled': {
                            opacity: isRegisteredInstance ? 1 : 0.5,
                            ...(isRegisteredInstance && {
                              color: 'common.white',
                              bgcolor: 'success.main',
                              borderColor: 'success.main',
                            }),
                          },
                        }}
                      />
                    );
                  })
                ) : course.startDate ? (
                  <Chip
                    label={formatDateTime(course.startDate) || 'No date available'}
                    color="primary"
                    variant="filled"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      minHeight: { xs: 44, sm: 36 },
                      py: { xs: 1, sm: 0.5 },
                      justifyContent: 'flex-start',
                      fontSize: '14px',
                      borderRadius: '100px',
                      '& .MuiChip-label': { whiteSpace: 'normal', display: 'block' },
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">No upcoming dates available.</Typography>
                )}
              </Box>

              {/* CTA */}
              <Box sx={{ px: 2 }}>
                {accessLoading || addonCheckLoading || regLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : !hasAccess && isTenantManaged ? (
                  <TenantUpgradeCtaComponent
                    hasPendingRequest={addonCheck?.hasPendingRequest ?? false}
                    loading={requestUpgradeLoading}
                    success={requestUpgradeSuccess}
                    onRequest={onRequestUpgrade}
                    isAdmin={isTenantAdminProp && isTenantManaged}
                    seatAvailable={adminSelfAssign?.seatAvailable ?? null}
                    activating={adminSelfAssign?.activating}
                    activated={adminSelfAssign?.activated}
                    activateError={adminSelfAssign?.error}
                    onActivate={onAdminActivate}
                    onManageBilling={() => navigate('/app/tenant-admin/billing')}
                    moduleLabel="Webinar"
                  />
                ) : !hasAccess ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.disabled' }}>
                      <LockIcon sx={{ fontSize: 20 }} />
                      <Typography variant="body2" color="text.disabled">
                        Webinar access required
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Add the Webinar module to your subscription to register for live sessions.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      startIcon={<UpgradeIcon />}
                      onClick={() => navigate(isTenantAdmin ? '/app/tenant-admin/billing' : '/app/billing')}
                      sx={{ textTransform: 'uppercase', fontWeight: 500, letterSpacing: '0.46px' }}
                    >
                      Upgrade Subscription
                    </Button>
                  </Box>
                ) : isRegistered ? (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'success.light',
                      borderRadius: '8px',
                      bgcolor: 'rgba(46, 125, 50, 0.04)',
                      p: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.25,
                    }}
                  >
                    {/* Status row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: 'success.main',
                          }}
                        >
                          {isCompleted ? 'Completed' : 'Registered'}
                        </Typography>
                      </Box>
                      {isCompleted && (
                        <Chip
                          label={`${course.creditHours} CPE Credit${course.creditHours === 1 ? '' : 's'}`}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ height: 22, fontSize: '11px', borderRadius: '100px' }}
                        />
                      )}
                    </Box>

                    <Divider sx={{ borderColor: 'success.light' }} />

                    {/* Session date/time */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CalendarIcon sx={{ fontSize: 15, color: 'text.secondary', mt: '1px', flexShrink: 0 }} />
                      <Box>
                        <Typography sx={{ fontSize: '10px', color: 'text.secondary', lineHeight: 1.4, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>
                          Session
                        </Typography>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.primary', lineHeight: 1.4 }}>
                          {activeStartDate ? formatDateTime(activeStartDate) : 'TBD'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Completed at date */}
                    {isCompleted && currentRegistration?.completedAt && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 15, color: 'success.main', mt: '1px', flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ fontSize: '10px', color: 'text.secondary', lineHeight: 1.4, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>
                            Completed
                          </Typography>
                          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.primary', lineHeight: 1.4 }}>
                            {formatDateTime(currentRegistration.completedAt)}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Certificate link */}
                    {isCompleted && currentRegistration?.certificateUrl && (
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={handleDownloadCertificate}
                        disabled={isDownloadingCertificate}
                        color="success"
                        startIcon={isDownloadingCertificate
                          ? <CircularProgress size={16} color="inherit" />
                          : <CertificateIcon sx={{ fontSize: '16px !important' }} />}
                        sx={{ textTransform: 'uppercase', fontWeight: 500, letterSpacing: '0.46px', fontSize: '13px' }}
                      >
                        {isDownloadingCertificate ? 'Downloading…' : 'View Certificate'}
                      </Button>
                    )}

                    {/* Join button */}
                    {!isCompleted && (
                      currentRegistration?.joinUrl ? (
                        <Button
                          variant="contained"
                          color="primary"
                          size="medium"
                          fullWidth
                          startIcon={<JoinIcon />}
                          onClick={handleJoinWebinar}
                          sx={{ textTransform: 'uppercase', fontWeight: 500, letterSpacing: '0.4px', fontSize: '14px' }}
                        >
                          Join Webinar
                        </Button>
                      ) : (
                        <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.4 }}>
                            Opens {activeStartDate
                              ? new Date(parseApiDateAsUtc(activeStartDate).getTime()).toLocaleString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  timeZoneName: 'short',
                                })
                              : 'soon'}
                          </Typography>
                        </Box>
                      )
                    )}

                    {!isCompleted && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        fullWidth
                        startIcon={isCancelling ? <CircularProgress size={16} color="inherit" /> : <CancelIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={handleCancelRegistration}
                        disabled={isCancelling || isTransferring}
                        sx={{ textTransform: 'uppercase', fontWeight: 500, fontSize: '13px', letterSpacing: '0.46px' }}
                      >
                        {isCancelling ? 'Cancelling…' : 'Cancel Registration'}
                      </Button>
                    )}

                    {/* Clear non-registered chip selection */}
                    {effectiveSelectedInstanceId !== null && effectiveSelectedInstanceId !== registeredInstanceId && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        fullWidth
                        startIcon={isTransferring ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={handleTransfer}
                        disabled={isTransferring || isCancelling}
                        sx={{ textTransform: 'uppercase', fontWeight: 500, fontSize: '13px', letterSpacing: '0.46px' }}
                      >
                        {isTransferring ? 'Transferring…' : 'Change Date'}
                      </Button>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      startIcon={isRegistering ? <CircularProgress size={18} color="inherit" /> : <WebinarIcon />}
                      onClick={handleRegister}
                      disabled={isRegistering || !effectiveSelectedInstanceId}
                      sx={{ textTransform: 'uppercase', fontWeight: 500, letterSpacing: '0.46px' }}
                    >
                      {isRegistering ? 'Registering…' : 'Register for Webinar'}
                    </Button>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Paper>
        </Box>

        {/* AI Assistant */}
        <Box
          sx={{
            gridArea: 'ai',
            minWidth: 0,
            alignSelf: 'start',
            position: { xl: 'sticky' },
            top: { xl: 0 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <AIAssistant
              variant="embedded"
              title="Need help with this course?"
              subtitle="Ask for a summary, related learning ideas, or what to focus on next."
              courseContext={aiCourseContext}
              suggestions={[
                { id: 'course-summary', text: `Summarize what I'll learn in ${course.title}` },
                { id: 'course-fit', text: `How does this course support my ${fieldsOfStudy[0] || 'CPE'} goals?` },
                { id: 'similar-courses', text: `Find similar webinars as ${course.title} for me` },
              ]}
            />
          </Paper>
        </Box>

        </Box>{/* end sidebar wrapper */}
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Confirmation dialog for cancel / transfer */}
      <Dialog
        open={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" sx={{ fontSize: 22 }} />
          {pendingAction === 'cancel' ? 'Cancel Registration' : 'Change Date'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            {pendingAction === 'cancel'
              ? 'Are you sure you want to cancel your registration?'
              : 'Are you sure you want to switch to a different date?'}
          </Typography>
          <Alert severity="warning" sx={{ fontSize: '14px' }}>
            Once {pendingAction === 'cancel' ? 'cancelled' : 'changed'}, the current date will be
            permanently unavailable for re-registration.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPendingAction(null)}>
            Go Back
          </Button>
          <Button
            variant="contained"
            color={pendingAction === 'cancel' ? 'error' : 'primary'}
            onClick={pendingAction === 'cancel' ? confirmCancelRegistration : confirmTransfer}
          >
            {pendingAction === 'cancel' ? 'Cancel Registration' : 'Change Date'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
